import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ApiService,
  IDeliveryCheckInfo,
  IPaginatedResponse,
  isPiecesOrPiecesGroup,
  TransferableGetApiService,
  IUpsellProductType, IProductType, IProductTypeOverview, IProductAllergen
} from '@cakeiteasy/web-utils';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, firstValueFrom } from 'rxjs';
import { TransferState } from '@angular/core';
import { map } from 'rxjs/operators';
import { IFilterCategory } from '../../cakes-overview/models/i-filter-category';

@Injectable()
export class ProductsApiService extends TransferableGetApiService {
  constructor(@Inject('AppConfig') appConfig: any,
              @Inject(PLATFORM_ID) _platformId: any,
              api: ApiService,
              transferState: TransferState) {
    super(api, appConfig, _platformId, transferState);
  }

  public getProductType(productTypeId: number, params?: any): Promise<IProductType> {
    return firstValueFrom(this.getWithCaching('api.products', 'product_types_one', { id: productTypeId }, { params }))
      .then((cached) => {
        // The cache hands every caller the same object. The builder mutates it (minPrice, price diffs) and puts its
        // sizes into cart lines, which the store freezes in dev mode, so each caller gets its own copy.
        const productType: IProductType = cloneDeep(cached);
        productType.sizes = productType.sizes
          .filter((size) => size.products.length > 0);

        /** @Deprecated */
        productType.pieces = isPiecesOrPiecesGroup(productType.structureType);

        return productType;
      });
  }

  public getProductTypeUpsellProducts(productTypeId: number, params?: any): Promise<IUpsellProductType[]> {
    return firstValueFrom(this.getWithCaching('api.products', 'product_types_upsell_products_one', { id: productTypeId }, { params }));
  }

  public getProductTypes<T = IProductTypeOverview>(params): Observable<T[]> {
    return this.getWithCaching('api.products', 'product_types', {}, {
      params: { ...params, t: Date.now() }
    })
      .pipe(
        map((productTypes) => {
          productTypes.forEach((pt) => {
            /** @Deprecated */
            pt.pieces = isPiecesOrPiecesGroup(pt.structureType);
          });

          return productTypes;
        })
      );
  }


  public getFilters(params): Promise<any> {
    return firstValueFrom(this.getWithCaching('api.products', 'product_types_filters', {}, { params }));
  }

  public getAllergens(params): Promise<IProductAllergen[]> {
    return firstValueFrom(this.getWithCaching('api.products', 'product_types_allergens', {}, { params }));
  }

  // Should be renamed to getCategoriesByBakeryId
  public getCategories(bakeryId, params = {}) {
    return firstValueFrom(this.getWithCaching('api.bakery', 'bakeries_categories', { id: bakeryId }, { params }));
  }

  public getCategoriesByRefName(refName: string, params: {}): Promise<IPaginatedResponse<IFilterCategory>> {
    return firstValueFrom(this.getWithCaching('api.categories', 'categories_by_ref_name', { refName }, { params }));
  }

  public getProductTypeDeliverDate(productTypeId: number, date: Date): Promise<IDeliveryCheckInfo> {
    const utcDate = moment(date).format('YYYY-MM-DD');
    return firstValueFrom(this.api
      .get('api.products', 'product_types_one_closest_delivery_date',
        {
          id: productTypeId
        }, {
          params: {
            deliveryDate: utcDate
          }
        }))
      .then((res) => {
        const jsDate = moment(res.date).toDate();
        if (res.date === utcDate) {
          return res;
        } else {
          return { ...res, closestDate: jsDate, date: null };
        }
      })
      .catch(() => {
        return {
          date: null,
          closestDate: null,
          outletExists: false,
          deliveryPointExists: false
        };
      });
  }
}
